import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { fetchDevUsers, activateDevUser, logout, type DevUser } from '../api/client'
import { useHeaderTitle } from './HeaderTitleContext'
import { useUiLanguage } from '../hooks/useUiLanguage'
import { LanguageSwitcher } from './LanguageSwitcher'

// Maps a route path to a translation key under `header.titles`.
const pageTitleKeys: Record<string, string> = {
  '/exams': 'exams',
  '/exams/new': 'examsNew',
  '/exams-c/new': 'examsCNew',
  '/exams-d/new': 'examsDNew',
  '/classes': 'classes',
  '/subscribers': 'subscribers',
  '/members': 'members',
  '/costs': 'costs',
  '/llm-settings': 'llmSettings',
  '/admin/stats': 'stats',
  '/analytics': 'analytics',
  '/profile': 'profile',
}

interface MeResponse {
  id: string
  email: string
  full_name: string | null
  user_role: string
  membership_role: string | null
  feedback_language: string
  ui_language?: 'tr' | 'nl' | 'en' | null
}

const initials = (u: { email: string; full_name: string | null }) =>
  (u.full_name ? u.full_name.split(' ').map(w => w[0]).join('').slice(0, 2) : u.email[0]).toUpperCase()

export const Header = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { syncFromUser } = useUiLanguage()
  const { title: overrideTitle, setTitle } = useHeaderTitle()
  const [me, setMe] = useState<MeResponse | null>(null)
  const [devUsers, setDevUsers] = useState<DevUser[]>([])
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const roleBadge = (user: MeResponse) => {
    if (user.user_role === 'ADMIN') return { label: t('header.roles.admin'), cls: 'role-badge--admin' }
    if (user.membership_role === 'ORG_MANAGER') return { label: t('header.roles.manager'), cls: 'role-badge--manager' }
    if (user.membership_role === 'TEACHER') return { label: t('header.roles.teacher'), cls: 'role-badge--teacher' }
    return { label: user.user_role || '—', cls: 'role-badge--default' }
  }

  const loadMe = () => {
    fetch('/api/me', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setMe(d); void syncFromUser(d) } })
      .catch(() => {})
  }

  useEffect(() => {
    loadMe()
    fetchDevUsers().then(setDevUsers).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setTitle(null)
  }, [location.pathname, setTitle])

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleActivate = async (email: string) => {
    setOpen(false)
    await activateDevUser(email)
    window.location.reload()
  }

  const handleLogout = async () => {
    setOpen(false)
    try {
      await logout()
    } catch {
      // ignore — clear session anyway
    }
    navigate('/login', { replace: true })
  }

  const titleKey = pageTitleKeys[location.pathname]
  let title = titleKey ? t(`header.titles.${titleKey}`) : t('header.titles.dashboard')
  if (location.pathname.match(/^\/exams\/\d+\/setup$/)) title = t('header.titles.examSettings')
  if (location.pathname.match(/^\/exams\/\d+\/edit$/)) title = t('header.titles.examEdit')
  if (overrideTitle) title = overrideTitle

  const badge = me ? roleBadge(me) : null

  return (
    <header className="header">
      <h1 className="header-title">{title}</h1>

      <div className="header-right">
        <LanguageSwitcher variant="header" />

        {me && (
          <div className="user-switcher" ref={dropdownRef}>
            <button className="user-switcher-btn" onClick={() => setOpen(v => !v)}>
              <div className="user-avatar">{initials(me)}</div>
              <div className="user-info">
                <div className="user-info-name">{me.full_name || me.email}</div>
                {badge && (
                  <div className={`role-badge ${badge.cls}`} style={{ marginTop: 2 }}>
                    {badge.label}
                  </div>
                )}
              </div>
            </button>

            {open && (
              <div className="user-dropdown">
                <button
                  className="user-dropdown-item"
                  onClick={() => { setOpen(false); navigate('/profile'); }}
                >
                  <div className="user-dropdown-item-info">
                    <div className="user-dropdown-item-name">{t('header.menu.profile')}</div>
                    <div className="user-dropdown-item-email">{t('header.menu.profileSubtitle')}</div>
                  </div>
                </button>

                {/* Dev-mode user switcher */}
                {devUsers.length > 0 && (
                  <>
                    <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 0' }} />
                    <div style={{ padding: '4px 12px 2px', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('header.menu.devSwitchUser')}
                    </div>
                    {devUsers.map(u => (
                      <button
                        key={u.id}
                        className={`user-dropdown-item${u.email === me?.email ? ' active' : ''}`}
                        onClick={() => handleActivate(u.email)}
                      >
                        <div className="user-avatar">{initials(u)}</div>
                        <div className="user-dropdown-item-info">
                          <div className="user-dropdown-item-name">{u.full_name || u.email}</div>
                          <div className="user-dropdown-item-email">{u.email}</div>
                        </div>
                        {u.user_role === 'ADMIN' && (
                          <span className="role-badge role-badge--admin" style={{ marginLeft: 'auto' }}>{t('header.roles.admin')}</span>
                        )}
                      </button>
                    ))}
                  </>
                )}

                {/* Logout */}
                <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 0' }} />
                <button
                  className="user-dropdown-item"
                  onClick={handleLogout}
                  style={{ color: 'var(--color-danger)' }}
                >
                  <div className="user-dropdown-item-info">
                    <div className="user-dropdown-item-name">{t('header.menu.logout')}</div>
                  </div>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
